import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Button, Checkbox, Form, Row, Select, Space, Tooltip, Typography } from 'antd';
import {
    DropboxOutlined,
    InfoCircleOutlined,
    MinusCircleOutlined,
    MinusOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { applyLang } from '../layout';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

const { Text, Title } = Typography;

function CustomTooltip(props) {
    if (!props.title) {
        return props.children;
    }

    var tooltipProps = { ...props };
    tooltipProps.trigger = props.trigger || "click";

    return (
        <div style={{ display: "flex" }}>
            {props.children}
            <div style={{ marginLeft: 5 }}>
                <Tooltip {...tooltipProps}>
                    <InfoCircleOutlined style={{ color: "rgb(114, 132, 154)", fontSize: 14 }} />
                </Tooltip>
            </div>
        </div>
    );
}

const ProductField = ({ value, onChange, product, formPlugin, liveRender, device }) => {
    const withPropagation = useRef(true);
    const [stock, setStock] = useState(Infinity);
    const [_updateValue, forceUpdate] = useReducer(x => x + 1, 0);
    const calendarsValues = JSON.stringify(formPlugin.getCalendarsValues());

    useEffect(() => {
        formPlugin.getAvailableProduct(product.id, value, withPropagation.current).then(total => {
            if (total !== stock) setStock(total);
        });
        withPropagation.current = true;
    }, [value, calendarsValues, _updateValue]);

    useEffect(() => {
        formPlugin.addProductStockRef(product.id, () => {
            withPropagation.current = false;
            forceUpdate();
        });
    }, []);

    const finalValue = value || 0;
    const isOver = (stock <= 0 && finalValue === 0);
    const isAvailable = stock - 1 >= 0;

    if (finalValue > (stock + finalValue)) {
        onChange(stock <= 0 ? 0 : stock);
    }

    const setValue = (v) => {
        if (v < 0 || (v > finalValue && !isAvailable)) return false;
        onChange(v);
    };

    if (isOver && product?.hideOverStock) return null;

    const isAbo=formPlugin.formData?.paiementType==="MONTHLY"

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: "10px 5%",
            paddingTop: 20,
            backgroundColor: isOver ? "#EDEDED" : "transparent",
            borderRadius: 10,
            border: "1px solid #1D1D1B33",
            userSelect: "none",
        }}>
            <div>
                <Title className={"layout-label"} level={device === "MOBILE" ? 5 : 4}>
                    <CustomTooltip title={product.description}>{product.title}</CustomTooltip>
                </Title>
            </div>

            <Row style={{ justifyContent: "space-between", alignItems: "center", padding: device === "MOBILE" ? 0 : "0 20px" }}>
                <Space>
                    {formPlugin.formData.withPaiement && product.montant > 0 && (
                        <Text className={"layout-label"}>{formPlugin.currencyFormat(product.montant)} {isAbo && (<Trans>/ Mois</Trans>)}</Text>
                    )}
                    {stock < Infinity && !isOver && (
                        <Text className={"layout-label"} type="secondary" style={{ fontSize: device === "MOBILE" ? 12 : 15 }}>
                            {stock} <Trans>disponible</Trans>
                        </Text>
                    )}
                    {isOver && (
                        <Text className={"layout-label"} type="secondary" style={{ color: "red", fontSize: device === "MOBILE" ? 12 : 15 }}>
                            <Trans>Rupture de stock</Trans>
                        </Text>
                    )}
                </Space>

                <div style={{
                    width: 95,
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: 5,
                }}>
                    <div
                        style={{
                            backgroundColor: ((liveRender || isOver) && finalValue > 0) ? "#000" : "rgba(0,0,0,0.2)",
                            color: "#FFF",
                            borderRadius: "100%",
                            width: 28,
                            height: 28,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: ((liveRender || isOver) && finalValue > 0) ? "pointer" : "auto"
                        }}
                        onClick={(liveRender || isOver) ? () => setValue(finalValue - 1) : null}
                    >
                        <MinusOutlined />
                    </div>

                    <span style={{ fontSize: 16 }}>{finalValue}</span>

                    <div
                        style={{
                            backgroundColor: (liveRender || isOver || !isAvailable) ? "#000" : "rgba(0,0,0,0.2)",
                            color: "#FFF",
                            borderRadius: "100%",
                            width: 28,
                            height: 28,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: "pointer"
                        }}
                        onClick={(liveRender || isOver || !isAvailable) ? () => setValue(finalValue + 1) : null}
                    >
                        <PlusOutlined />
                    </div>
                </div>
            </Row>
        </div>
    );
};

const ProductSelect = ({ value, onChange, product, formPlugin, liveRender }) => {
    const withPropagation = useRef(true);
    const [stock, setStock] = useState(Infinity);
    const [_updateValue, forceUpdate] = useReducer(x => x + 1, 0);
    const calendarsValues = JSON.stringify(formPlugin.getCalendarsValues());

    useEffect(() => {
        formPlugin.getAvailableProduct(product.id, value, withPropagation.current).then(total => {
            if (total !== stock) setStock(total);
        });
        withPropagation.current = true;
    }, [value, calendarsValues, _updateValue]);

    useEffect(() => {
        formPlugin.addProductStockRef(product.id, () => {
            withPropagation.current = false;
            forceUpdate();
        });
    }, []);

    const finalValue = value || 0;
    const isOver = (stock <= 0 && finalValue === 0);
    const isAvailable = stock - 1 >= 0;

    let maxItems = 30;
    if (stock !== Infinity && (stock + finalValue) > 0 && (stock + finalValue) < maxItems) {
        maxItems = (stock + finalValue);
    }

    const isAbo=formPlugin.formData?.paiementType==="MONTHLY"

    const renderOptions = product?.renderOptions || {};
    const options = Array.from(Array(maxItems).keys()).map(index => {
        let _value = index + 1;
        let _label = "";

        if (renderOptions.selectPrefix) _label += renderOptions.selectPrefix + " ";
        _label += _value + " ";

        _label += _value === 1
            ? (renderOptions.selectProductName || product.title)
            : (renderOptions.selectProductNameMulti || product.title);


        if (product.montant) {
            if(isAbo){
                _label += t` (${formPlugin.currencyFormat(product.montant * _value)} / Mois)`;
            }else{
                _label += " (" + formPlugin.currencyFormat(product.montant * _value) + ")";
            }
        }

        return { value: _value, label: _label };
    });

    if (isOver && product?.hideOverStock) return null;


    return (
        <select onChange={(e) => onChange(parseInt(e.target.value))} value={finalValue} className={"form-control"}
                disabled={(stock !== Infinity && isOver && liveRender)}>
            <option value={0}>{!isOver && t`Choisir une option`} {isOver && t`Rupture de stock`}</option>
            {options.map(opt => <option value={opt.value}>{opt.label}</option>)}
        </select>
    );
};


const ProductCheckbox = ({ value, product, formPlugin }) => {
    const [stock, setStock] = useState(Infinity);
    const calendarsValues = JSON.stringify(formPlugin.getCalendarsValues());

    useEffect(() => {
        formPlugin.getAvailableProduct(product.id, value).then(total => {
            if (total !== stock) setStock(total);
        });
    }, [value, calendarsValues]);

    const isOver = stock <= 0;
    const renderOptions = product?.renderOptions || {};

    const isAbo=formPlugin.formData?.paiementType==="MONTHLY"

    let title = product.title;
    if (product.montant) {
        title += " - " + formPlugin.currencyFormat(product.montant);
        if(isAbo){
            title += t` / Mois`;
        }
    }

    let checkBoxLabel = renderOptions?.checkboxLabel || title;

    if (isOver && product?.hideOverStock) return null;

    const CheckBoxComponent = (_props)=>
    {
        const [checked, setChecked] = useState(_props.value===1);
        let _value=checked?1:0

        useEffect(() => {
            if(_value!==_props.value){
                _props.onChange(_value);
            }
        },[checked])

        useEffect(() => {
            if(_value!==_props.value){
               setChecked(_props.value?true:false)
            }
        },[_props.value]);

        return(
            <Checkbox checked={checked} onChange={(e)=>setChecked(e.target.checked)} disabled={isOver}>
                <div style={{userSelect:"none"}} className={"layout-label"}>{checkBoxLabel}</div>
            </Checkbox>
        )
    }

    return (
        <Form.Item
            name={product.id + '_quantity'}
            label={(
                <Space>
                    {title}
                    {isOver && (
                        <Text className={"layout-label"} style={{ color: "red" }}>
                            <Trans>Rupture de stock</Trans>
                        </Text>
                    )}
                </Space>
            )}
        >
            <CheckBoxComponent/>
        </Form.Item>
    );
};

export default (formPlugin, namespace) => {
    return {
        Renderer: (props) => {
            if (props.remove) formPlugin.onAddFormPlugin(props, props.nodeId, props.pluginConfig.id);
            const { data } = props;
            const liveRender = (props.isPreviewMode || formPlugin.live);
            const productList = data.productList || [];

            if (formPlugin.formData.type !== "COMPLEX") {
                if (props.remove) props.remove();
                return null;
            }

            const renderProduct = (product) => {
                product = applyLang(product, { value: props.lang });
                const type = product?.renderOptions?.type || "DEFAULT";

                if (type === "SELECT") {
                    return (
                        <Form.Item
                            label={product.title}
                            tooltip={product.description ? { title: product.description, icon: <InfoCircleOutlined /> } : null}
                            name={product.id + '_quantity'}
                        >
                            <ProductSelect product={product} formPlugin={formPlugin} liveRender={liveRender} />
                        </Form.Item>
                    );
                }

                if (type === "CHECKBOX") {
                    return <ProductCheckbox product={product} formPlugin={formPlugin} />;
                }

                return (
                    <Form.Item name={product.id + '_quantity'}>
                        <ProductField product={product} formPlugin={formPlugin} liveRender={liveRender} device={props.device} />
                    </Form.Item>
                );
            };

            return (
                <div style={{
                    minHeight: liveRender ? 0 : 50,
                    border: liveRender ? 0 : "1px dashed red",
                    borderRadius: liveRender ? 0 : "10px"
                }}>
                    {productList.map(p => {
                        if (!p) return null;
                        let product = formPlugin.formData.products.find(i => i.id === p.productId);
                        if (product && product.active) return renderProduct(product);
                    })}
                </div>
            );
        },
        namespace,
        id: 'form-product' + namespace,
        title: t`Produit`,
        description: t`Élément de formulaire utilisable.`,
        icon: <DropboxOutlined />,
        iconColor: "#8E44AD",
        version: 1,
        hideInMenu: formPlugin.formData.type !== "COMPLEX",
        allowClickInside: false,
        controls: {
            type: 'custom',
            Component: (props) => {
                const products = formPlugin.formData.products;
                const productList = props.data?.productList || [];
                const defaultValue = { productList: productList };

                return (
                    <Form initialValues={defaultValue} onValuesChange={(_, values) => props.onChange(values)}>
                        <Form.List name={"productList"} label={t`Produits`}>
                            {(fields, { add, remove }) => (
                                <div>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Form.Item name={[name, "productId"]} label={t`Produit`} required>
                                                <Select placeholder={t`Choisir un produit`}>
                                                    {products.map(i => (
                                                        <Select.Option value={i.id}>{i.label}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(name)} />
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            <Trans>Ajouter un produit</Trans>
                                        </Button>
                                    </Form.Item>
                                </div>
                            )}
                        </Form.List>
                    </Form>
                );
            }
        },
    };
};
